<template>
  <header class="erp-header">
    <!-- Left: Mobile toggle + breadcrumb -->
    <div class="header-left">
      <button class="mobile-toggle" @click="$emit('toggle-mobile-menu')" aria-label="Toggle navigation">
        <i class="bi bi-list"></i>
      </button>
      <span class="page-title">{{ pageTitle }}</span>
    </div>

    <!-- Right: Tools dropdown -->
    <div class="header-right">

      <!-- Notification placeholder -->
      <button class="icon-btn" title="Notifications">
        <i class="bi bi-bell"></i>
        <span class="badge-dot"></span>
      </button>

      <!-- Tools Dropdown -->
      <div class="dropdown" ref="dropdownRef">
        <button
          class="tools-btn"
          id="tools-dropdown-btn"
          @click="dropdownOpen = !dropdownOpen"
          :aria-expanded="dropdownOpen"
        >
          <i class="bi bi-gear-fill"></i>
          <span>{{ $t('tools') }}</span>
          <i class="bi bi-chevron-down chevron" :class="{ rotated: dropdownOpen }"></i>
        </button>

        <div class="dropdown-panel" v-show="dropdownOpen">

          <!-- Language -->
          <div class="panel-section">
            <p class="panel-label">{{ $t('language') }}</p>
            <div class="lang-btns">
              <button
                class="lang-btn"
                :class="{ active: locale === 'en' }"
                @click="changeLang('en')"
              >🇬🇧 English</button>
              <button
                class="lang-btn"
                :class="{ active: locale === 'am' }"
                @click="changeLang('am')"
              >🇪🇹 አማርኛ</button>
            </div>
          </div>

          <div class="panel-divider"></div>

          <!-- Theme -->
          <div class="panel-section">
            <p class="panel-label">{{ $t('selectTheme') }}</p>
            <div class="theme-grid">
              <button
                v-for="t in themes"
                :key="t.name"
                class="swatch-btn"
                :class="{ active: currentTheme === t.name }"
                :title="t.label"
                @click="changeTheme(t.name)"
              >
                <span class="swatch" :style="{ background: t.color }"></span>
                <span class="swatch-label">{{ t.label }}</span>
              </button>
            </div>
          </div>

          <div class="panel-divider"></div>

          <!-- About -->
          <router-link to="/about-us" class="panel-item" @click="dropdownOpen = false">
            <i class="bi bi-info-circle"></i>
            <span>{{ $t('aboutUs') }}</span>
          </router-link>

          <div class="panel-divider"></div>

          <!-- Logout -->
          <button class="panel-item logout-item" @click="logout">
            <i class="bi bi-box-arrow-right"></i>
            <span>Logout</span>
          </button>

        </div>
      </div>

    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { company } from '../api/axios'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const router = useRouter()
const route = useRoute()

const dropdownOpen = ref(false)
const dropdownRef = ref(null)

// Page title from route
const pageTitle = computed(() => {
  const map = {
    '/': 'Dashboard',
    '/products': 'Products',
    '/categories': 'Categories',
    '/stock': 'Stock',
    '/warehouses': 'Warehouses',
    '/purchase-orders': 'Purchase Orders',
    '/sales-orders': 'Sales Orders',
    '/suppliers': 'Suppliers',
    '/customers': 'Customers',
    '/reports': 'Reports',
    '/about-us': 'About Us',
  }
  return map[route.path] ?? company.name
})

// Themes with display labels and representative accent colors for the swatch
const themes = [
  { name: 'cerulean',   label: 'Cerulean',   color: '#2fa4e7' },
  { name: 'cosmo',      label: 'Cosmo',      color: '#2780e3' },
  { name: 'flatly',     label: 'Flatly',     color: '#18bc9c' },
  { name: 'journal',    label: 'Journal',    color: '#eb6864' },
  { name: 'litera',     label: 'Litera',     color: '#4582ec' },
  { name: 'lumen',      label: 'Lumen',      color: '#158cba' },
  { name: 'lux',        label: 'Lux',        color: '#1a1a1a' },
  { name: 'materia',    label: 'Materia',    color: '#2196f3' },
  { name: 'minty',      label: 'Minty',      color: '#78c2ad' },
  { name: 'morph',      label: 'Morph',      color: '#378dfc' },
  { name: 'pulse',      label: 'Pulse',      color: '#593196' },
  { name: 'quartz',     label: 'Quartz',     color: '#6460f4' },
  { name: 'sandstone',  label: 'Sandstone',  color: '#93765d' },
  { name: 'simplex',    label: 'Simplex',    color: '#d9230f' },
  { name: 'sketchy',    label: 'Sketchy',    color: '#333333' },
  { name: 'slate',      label: 'Slate',      color: '#3a3f44' },
  { name: 'solar',      label: 'Solar',      color: '#b58900' },
  { name: 'spacelab',   label: 'Spacelab',   color: '#446e9b' },
  { name: 'superhero',  label: 'Superhero',  color: '#4e5d6c' },
  { name: 'united',     label: 'United',     color: '#e95420' },
  { name: 'vapor',      label: 'Vapor',      color: '#6f42c1' },
  { name: 'yeti',       label: 'Yeti',       color: '#008cba' },
  { name: 'zephyr',     label: 'Zephyr',     color: '#3459e6' },
  { name: 'cyborg',     label: 'Cyborg',     color: '#2a9fd6' },
  { name: 'darkly',     label: 'Darkly',     color: '#375a7f' },
]
const currentTheme = ref(localStorage.getItem('app_theme') || 'cerulean')

function changeTheme(name) {
  currentTheme.value = name
  localStorage.setItem('app_theme', name)
  const link = document.getElementById('bootswatch-theme')
  if (link) {
    link.href = `https://cdn.jsdelivr.net/npm/bootswatch@5/dist/${name}/bootstrap.min.css`
  }
  dropdownOpen.value = false
}

function changeLang(lang) {
  locale.value = lang
  localStorage.setItem('app_locale', lang)
  dropdownOpen.value = false
}

function logout() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('username')
  localStorage.removeItem('user_role')
  localStorage.removeItem('user_email')
  localStorage.removeItem('company')
  dropdownOpen.value = false
  router.push('/login')
}

// Close dropdown when clicking outside
function handleOutsideClick(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    dropdownOpen.value = false
  }
}

onMounted(() => document.addEventListener('mousedown', handleOutsideClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', handleOutsideClick))
</script>

<style scoped>
.erp-header {
  height: 56px;
  background: var(--bs-body-bg, #fff);
  border-bottom: 1px solid var(--bs-border-color, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.25rem;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 90;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

/* Left */
.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.mobile-toggle {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--bs-secondary-color, #64748b);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  display: none;
  line-height: 1;
}

.mobile-toggle:hover { background: var(--bs-secondary-bg, #f1f5f9); color: var(--bs-body-color, #0f172a); }

@media (max-width: 767px) {
  .mobile-toggle { display: flex; align-items: center; }
}

.page-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--bs-body-color, #1e293b);
}

/* Right */
.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon-btn {
  position: relative;
  background: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: var(--bs-secondary-color, #64748b);
  cursor: pointer;
  transition: background 0.15s;
}

.icon-btn:hover { background: var(--bs-secondary-bg, #f1f5f9); color: var(--bs-body-color, #0f172a); }

.badge-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  background: #ef4444;
  border-radius: 50%;
  border: 1px solid #fff;
}

/* Tools button */
.dropdown {
  position: relative;
}

.tools-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--bs-secondary-bg, #f8fafc);
  border: 1px solid var(--bs-border-color, #e2e8f0);
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--bs-body-color, #334155);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.tools-btn:hover {
  background: var(--bs-tertiary-bg, #f1f5f9);
  border-color: var(--bs-border-color-translucent, #cbd5e1);
}

.tools-btn i:first-child {
  color: #3b82f6;
}

.chevron {
  font-size: 0.65rem;
  transition: transform 0.2s;
  color: var(--bs-secondary-color, #94a3b8);
}

.chevron.rotated {
  transform: rotate(180deg);
}

/* Dropdown panel */
.dropdown-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 340px;
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
  z-index: 200;
  overflow: hidden;
}

.panel-section {
  padding: 0.75rem 1rem;
}

.panel-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--bs-secondary-color, #94a3b8);
  margin: 0 0 0.5rem 0;
}

.lang-btns {
  display: flex;
  gap: 0.5rem;
}

.lang-btn {
  flex: 1;
  padding: 0.4rem;
  font-size: 0.8rem;
  border: 1px solid var(--bs-border-color, #e2e8f0);
  border-radius: 6px;
  background: var(--bs-secondary-bg, #f8fafc);
  cursor: pointer;
  transition: all 0.15s;
  color: var(--bs-body-color, #334155);
}

.lang-btn:hover { background: #eff6ff; border-color: #93c5fd; }
.lang-btn.active { background: #dbeafe; border-color: #3b82f6; color: #1d4ed8; font-weight: 600; }

/* Theme swatch grid */
.theme-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.4rem;
}

.swatch-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.3rem 0.2rem;
  background: none;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.1s;
}

.swatch-btn:hover {
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.swatch-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
}

.swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.08);
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  flex-shrink: 0;
}

.swatch-label {
  font-size: 0.58rem;
  font-weight: 600;
  color: var(--bs-secondary-color, #64748b);
  text-align: center;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 42px;
}

.swatch-btn.active .swatch-label {
  color: #1d4ed8;
}

.theme-select {
  display: none;
}

.panel-divider {
  height: 1px;
  background: var(--bs-border-color, #f1f5f9);
  margin: 0;
}

.panel-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 1rem;
  font-size: 0.87rem;
  color: var(--bs-body-color, #334155);
  text-decoration: none;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.panel-item:hover { background: var(--bs-secondary-bg, #f8fafc); }

.panel-item i { color: var(--bs-secondary-color, #64748b); }

.logout-item { color: #dc2626; }
.logout-item:hover { background: #fef2f2; }
.logout-item i { color: #dc2626; }
</style>
